import { useState, useCallback } from 'react';
import type { CheckoutCart, PaymentMethods } from '../../types/checkout';
import { WholesaleSurchargeNotice } from './WholesaleSurchargeNotice';

interface PaymentSectionProps {
  cart: CheckoutCart;
  paymentMethods: PaymentMethods;
  accRecOverLimit: boolean;
  accRecName: string | null;
  mercadoPagoEnabled: boolean;
  selectedMethod: number | null;
  comment: string;
  onSelectMethod: (method: number) => void;
  onQrSelected?: (useQr: boolean) => void;
  onCommentChange: (comment: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  error: string | null;
  directPaymentDiscount?: { percent: number; savings: number } | null;
  showQrOption?: boolean;
  /** Wholesale Mercado Pago surcharge rate; 0 when the cart has none. */
  wholesaleSurchargePercent?: number;
  /**
   * The surcharge in money, already resolved against the selected method by the
   * caller (0 when it does not apply). Same convention as CartSummary so both
   * summaries always show the same number.
   */
  wholesaleSurcharge?: number;
}

const CONTADO = 0;
const MERCADO_PAGO = 1;
const CUENTA_CORRIENTE = 2;
const TRANSFERENCIA_BANCARIA = 5;

const PAYMENT_ICONS: Record<number, string> = {
  [CONTADO]: 'cash',
  [MERCADO_PAGO]: 'mercadopago',
  [CUENTA_CORRIENTE]: 'account',
  [TRANSFERENCIA_BANCARIA]: 'bank_transfer',
};

function PaymentIcon({ type }: { type: string }) {
  switch (type) {
    case 'mercadopago':
      return (
        <img src="/img/mercadopago-mono.svg" alt="MercadoPago" className="h-6 w-6" />
      );
    case 'card':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      );
    case 'account':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
          <path d="M12 7v4M10 9h4" />
        </svg>
      );
    case 'cash':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="12" cy="12" r="3" />
          <path d="M2 10h2M20 10h2M2 14h2M20 14h2" />
        </svg>
      );
    case 'bank_transfer':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 21h18M3 7v14M21 7v14M6 21V11M12 21V11M18 21V11M6 7h12M12 3L22 7H2L12 3z" />
        </svg>
      );
    case 'qr':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="3" height="3" />
          <path d="M21 14h-3v3M21 21h-3v-4M18 21h3" />
        </svg>
      );
    default:
      return null;
  }
}

export function PaymentSection({
  cart,
  paymentMethods,
  accRecOverLimit,
  accRecName,
  mercadoPagoEnabled,
  selectedMethod,
  comment,
  onSelectMethod,
  onQrSelected,
  onCommentChange,
  onSubmit,
  isSubmitting,
  error,
  directPaymentDiscount,
  showQrOption,
  wholesaleSurchargePercent = 0,
  wholesaleSurcharge = 0,
}: PaymentSectionProps) {
  const [localError, setLocalError] = useState<string | null>(null);
  const [accRecAgreement, setAccRecAgreement] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(() => {
    // When pre-selected from quick checkout, resolve to the first matching key
    // to avoid highlighting all variants of the same method (e.g. 3 MercadoPago entries)
    if (selectedMethod !== null) {
      for (const [key, name] of Object.entries(paymentMethods)) {
        const methodId = parseInt(key, 10);
        if (methodId === selectedMethod) return key;
      }
    }
    return null;
  });

  const handleMethodSelect = useCallback((key: string, methodId: number, isQr = false) => {
    setLocalError(null);
    setSelectedKey(key);
    onSelectMethod(methodId);
    onQrSelected?.(isQr);
  }, [onSelectMethod, onQrSelected]);

  const handleSubmit = useCallback(() => {
    if (selectedMethod === null) {
      setLocalError('Selecciona un metodo de pago');
      return;
    }
    if (accRecOverLimit && !accRecAgreement) {
      setLocalError('Debes aceptar las condiciones para continuar');
      return;
    }
    setLocalError(null);
    onSubmit();
  }, [selectedMethod, accRecOverLimit, accRecAgreement, onSubmit]);

  const availableMethods: Array<{ key: string; methodId: number; name: string; iconType: string; isQr?: boolean; subtitle?: string }> = [];
  for (const [key, name] of Object.entries(paymentMethods)) {
    const methodId = parseInt(key, 10);
    if (methodId === MERCADO_PAGO && !mercadoPagoEnabled) continue;
    availableMethods.push({ key, methodId, name, iconType: PAYMENT_ICONS[methodId] || 'cash' });
    // Auto-duplicate MercadoPago as a "Tarjeta de crédito o débito" option
    if (methodId === MERCADO_PAGO) {
      availableMethods.push({ key: `${key}_card`, methodId: MERCADO_PAGO, name: 'Tarjeta de crédito o débito', iconType: 'card' });
      if (showQrOption) {
        availableMethods.push({ key: `${key}_qr`, methodId: MERCADO_PAGO, name: 'Pagar con QR', iconType: 'qr', isQr: true, subtitle: 'Escaneá con Mercado Pago u otra billetera' });
      }
    }
  }

  // Compute total including delivery
  const subtotal = cart.subtotal ?? cart.total;
  const discount = cart.discount ?? 0;
  const subscriptionDiscount = cart.subscriptionDiscount ?? 0;
  const replacementCreditDiscount = cart.replacementCreditDiscount ?? 0;
  const directDiscount = (selectedMethod === TRANSFERENCIA_BANCARIA && directPaymentDiscount?.savings) ? directPaymentDiscount.savings : 0;
  const grandTotal = subtotal - discount - subscriptionDiscount - replacementCreditDiscount - directDiscount + cart.deliveryPrice + wholesaleSurcharge;

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Selecciona como quieres pagar
      </p>

      {/* Order summary */}
      <div className="mb-4 rounded-xl bg-gray-50 p-3.5">
        <div className="flex justify-between py-1.5 text-sm text-gray-500">
          <span>Subtotal</span>
          <span>${subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
        </div>
        {subscriptionDiscount > 0 && (
          <div className="flex justify-between py-1.5 text-sm text-green-500">
            <span>{cart.subscriptionPlanName || 'Suscripción'}</span>
            <span>-${subscriptionDiscount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        {discount > 0 && (
          <div className="flex justify-between py-1.5 text-sm text-green-500">
            <span>Cupón</span>
            <span>-${discount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        {replacementCreditDiscount > 0 && (
          <div className="flex justify-between py-1.5 text-sm text-green-500">
            <span>Credito por reposicion</span>
            <span>-${replacementCreditDiscount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        {directDiscount > 0 && (
          <div className="flex justify-between py-1.5 text-sm text-emerald-600">
            <span>Dto. Transf. {directPaymentDiscount!.percent}%</span>
            <span>-${directDiscount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        {wholesaleSurcharge > 0 && (
          <div className="flex justify-between py-1.5 text-sm text-amber-700">
            <span>Recargo Mercado Pago mayoristas</span>
            <span>+${wholesaleSurcharge.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
          </div>
        )}
        <div className="flex justify-between py-1.5 text-sm text-gray-500">
          <span>Envio</span>
          <span>
            {cart.deliveryPrice > 0
              ? `$${cart.deliveryPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
              : 'Gratis'}
          </span>
        </div>
        <div className="mt-2 flex justify-between border-t border-gray-200 pt-3 text-base font-semibold text-gray-800">
          <span>Total</span>
          <span>${grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
        </div>
      </div>

      {/* Account receivable warning */}
      {accRecOverLimit && (
        <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-yellow-400 bg-amber-50 p-3 text-sm leading-relaxed text-amber-800">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0 text-amber-500">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div>
            <span>Has alcanzado el límite de tu cuenta corriente. Este pedido deberá ser abonado de contado o con pago online.</span>
            <label className="mt-2.5 flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={accRecAgreement}
                onChange={(e) => setAccRecAgreement(e.target.checked)}
                className="mt-0.5"
              />
              <span>Entiendo que este pedido no se cargará a mi cuenta corriente</span>
            </label>
          </div>
        </div>
      )}

      <WholesaleSurchargeNotice percent={wholesaleSurchargePercent} />

      {/* Payment methods */}
      <div className="mb-4 flex flex-col gap-2">
        {availableMethods.map(({ key, methodId, name, iconType, isQr, subtitle }) => {
          const isSelected = selectedKey ? selectedKey === key : selectedMethod === methodId;

          return (
            <button
              key={key}
              type="button"
              className={`flex w-full items-center gap-3 rounded-xl border-2 p-3.5 transition-all duration-200
                ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'}
                disabled:cursor-not-allowed disabled:opacity-60`}
              onClick={() => handleMethodSelect(key, methodId, isQr)}
              disabled={isSubmitting}
            >
              <div className="text-gray-500">
                <PaymentIcon type={iconType} />
              </div>
              <span className="flex-1 text-left">
                <span className="text-[15px] text-gray-800">
                  {name}
                  {methodId === TRANSFERENCIA_BANCARIA && directPaymentDiscount && directPaymentDiscount.savings > 0 && (
                    <span className="ml-2 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      Recomendado
                    </span>
                  )}
                </span>
                {methodId === CUENTA_CORRIENTE && accRecName && (
                  <span className="block text-xs text-gray-400">{accRecName}</span>
                )}
                {subtitle && (
                  <span className="block text-xs text-gray-400">{subtitle}</span>
                )}
              </span>
              <div className="flex-shrink-0">
                {isSelected ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#5cb85c">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="4" fill="white" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Direct payment discount banner */}
      {directPaymentDiscount && directPaymentDiscount.savings > 0 && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          💸 Pagando por transferencia ahorrás ${directPaymentDiscount.savings.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} en este pedido.
        </div>
      )}

      {/* Comment */}
      <div className="mb-4">
        <label htmlFor="checkout-comment" className="mb-1.5 block text-sm font-medium text-gray-800">
          Comentarios (opcional)
        </label>
        <textarea
          id="checkout-comment"
          className="w-full rounded-lg border border-gray-300 p-3 font-[inherit] text-[15px] focus:border-green-500 focus:outline-none"
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder="Instrucciones especiales para tu pedido..."
          rows={3}
          disabled={isSubmitting}
          style={{ resize: 'none' }}
        />
      </div>

      {/* Error */}
      {(error || localError) && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">
          {error || localError}
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        className="w-full rounded-lg bg-green-500 px-5 py-4 text-[17px] font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
        onClick={handleSubmit}
        disabled={isSubmitting || selectedMethod === null}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2.5">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            Procesando...
          </span>
        ) : (
          `Realizar pedido - $${grandTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
        )}
      </button>
    </div>
  );
}
